import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTrailStore } from '../stores/trailStore';

/**
 * NavigationTrail — a slim breadcrumb strip that builds up automatically as
 * the user drills down across detail pages (Session → Affair → Member, etc.).
 *
 * - Only visible when there are 2+ trail entries (so at least one is clickable).
 * - Sidebar navigation clears the trail entirely.
 * - Clicking a breadcrumb truncates the trail to that index and navigates back.
 */
export function NavigationTrail() {
  const trail = useTrailStore(s => s.trail);
  const truncateTo = useTrailStore(s => s.truncateTo);
  const navigate = useNavigate();

  // Need at least 2 entries so there's a clickable "back" entry
  if (trail.length < 2) return null;

  const handleClick = (index: number) => {
    truncateTo(index);
    navigate(trail[index].path);
  };

  return (
    <div
      style={{
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
        padding: '0 32px',
        height: '34px',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: '12px',
        flexShrink: 0,
      }}
    >
      {trail.map((entry, i) => {
        const isLast = i === trail.length - 1;
        return (
          <span
            key={`${entry.path}-${i}`}
            style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
          >
            {i > 0 && (
              <ChevronRight
                size={12}
                style={{ color: 'var(--text-3)', flexShrink: 0 }}
              />
            )}
            <button
              onClick={() => !isLast && handleClick(i)}
              disabled={isLast}
              style={{
                color: isLast ? 'var(--text-1)' : 'var(--text-2)',
                fontWeight: isLast ? 600 : 400,
                cursor: isLast ? 'default' : 'pointer',
                background: 'none',
                border: 'none',
                padding: '2px 6px',
                borderRadius: 'var(--r1)',
                fontSize: '12px',
                fontFamily: 'var(--font-display)',
                transition: 'color 0.15s, background 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                maxWidth: '320px',
              }}
              onMouseEnter={e => {
                if (!isLast) {
                  e.currentTarget.style.color = 'var(--accent)';
                  e.currentTarget.style.background = 'var(--accent-soft)';
                }
              }}
              onMouseLeave={e => {
                if (!isLast) {
                  e.currentTarget.style.color = 'var(--text-2)';
                  e.currentTarget.style.background = 'none';
                }
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                {entry.label}
              </span>
              {entry.filters && (
                <span style={{
                  fontSize: '10px',
                  color: 'var(--text-3)',
                  fontWeight: 400,
                  fontFamily: 'var(--font-mono)',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r1)',
                  padding: '1px 5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '140px',
                  flexShrink: 0,
                }}>
                  {entry.filters}
                </span>
              )}
            </button>
          </span>
        );
      })}
    </div>
  );
}
